import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from 'react-bootstrap'
import { isFeatureEnabled } from '../../../../config'

const SignupFlowDemo: React.FC = () => {
  const navigate = useNavigate()
  const [currentFlow, setCurrentFlow] = useState<'original' | 'new'>('original')
  const isNewFlowEnabled = isFeatureEnabled('USE_EMAIL_VERIFICATION_SIGNUP')

  const flowComparison = {
    original: {
      title: 'Legacy Signup Flow',
      route: '/auth/legacy-registration',
      features: [
        'Single-page form',
        'No email verification upfront',
        'Email verification popup when creating first experience',
        'Potential for duplicate accounts with OAuth',
        'Standard user experience'
      ],
      pros: [
        'Quick signup process',
        'Familiar to existing users',
        'No additional friction'
      ],
      cons: [
        'Email verification interrupts workflow',
        'Potential account duplication issues',
        'Security concerns with unverified emails'
      ]
    },
    new: {
      title: 'Multi-Step Verification Flow (Default)',
      route: '/auth/registration',
      features: [
        '3-step walkthrough process',
        'Email verification before account creation',
        'MFA-style code input interface',
        'Pre-verified email status',
        'No interruptions when creating experiences'
      ],
      pros: [
        'Enhanced security',
        'Better user experience post-signup',
        'Prevents duplicate account issues',
        'Modern, professional interface',
        'Clear progress indicators'
      ],
      cons: [
        'Additional step during signup',
        'Slightly longer initial process',
        'Requires email access during signup'
      ]
    }
  }

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-10">
          <div className="card shadow-sm">
            <div className="card-body p-8">
              <div className="text-center mb-8">
                <h1 className="text-dark fw-bolder mb-3">Signup Flow Comparison</h1>
                <p className="text-muted mb-4">
                  Compare both signup experiences side by side
                </p>
                
                <div className="alert alert-success">
                  <strong>Current Default:</strong> Multi-Step Email Verification Signup
                  <br />
                  <small>This is now the default experience at <code>/auth/registration</code>. Legacy flow available at <code>/auth/legacy-registration</code></small>
                </div>
              </div>

              <div className="row">
                {/* Original Flow */}
                <div className="col-md-6 mb-6">
                  <div className={`card h-100 ${currentFlow === 'original' ? 'border-primary' : ''}`}>
                    <div className="card-header bg-light">
                      <h4 className="card-title mb-0 d-flex align-items-center">
                        <i className="fas fa-user-plus text-primary me-2"></i>
                        {flowComparison.original.title}
                      </h4>
                    </div>
                    <div className="card-body">
                      <h6 className="fw-bold text-success mb-3">Features:</h6>
                      <ul className="list-unstyled mb-4">
                        {flowComparison.original.features.map((feature, index) => (
                          <li key={index} className="mb-2">
                            <i className="fas fa-check-circle text-success me-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>

                      <div className="row">
                        <div className="col-12 mb-3">
                          <h6 className="fw-bold text-primary">Pros:</h6>
                          <ul className="list-unstyled">
                            {flowComparison.original.pros.map((pro, index) => (
                              <li key={index} className="mb-1 small">
                                <i className="fas fa-plus text-success me-1"></i>
                                {pro}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="col-12">
                          <h6 className="fw-bold text-warning">Cons:</h6>
                          <ul className="list-unstyled">
                            {flowComparison.original.cons.map((con, index) => (
                              <li key={index} className="mb-1 small">
                                <i className="fas fa-minus text-warning me-1"></i>
                                {con}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className="card-footer">
                      <Button
                        className="btn btn-outline-secondary w-100"
                        onClick={() => {
                          setCurrentFlow('original')
                          navigate(flowComparison.original.route)
                        }}
                      >
                        Demo Legacy Flow
                      </Button>
                    </div>
                  </div>
                </div>

                {/* New Flow */}
                <div className="col-md-6 mb-6">
                  <div className={`card h-100 ${currentFlow === 'new' ? 'border-success' : ''}`}>
                    <div className="card-header bg-primary bg-opacity-10">
                      <h4 className="card-title mb-0 d-flex align-items-center">
                        <i className="fas fa-shield-alt text-primary me-2"></i>
                        {flowComparison.new.title}
                        <span className="badge bg-primary ms-2">Default</span>
                      </h4>
                    </div>
                    <div className="card-body">
                      <h6 className="fw-bold text-success mb-3">Features:</h6>
                      <ul className="list-unstyled mb-4">
                        {flowComparison.new.features.map((feature, index) => (
                          <li key={index} className="mb-2">
                            <i className="fas fa-star text-warning me-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>

                      <div className="row">
                        <div className="col-12 mb-3">
                          <h6 className="fw-bold text-primary">Pros:</h6>
                          <ul className="list-unstyled">
                            {flowComparison.new.pros.map((pro, index) => (
                              <li key={index} className="mb-1 small">
                                <i className="fas fa-plus text-success me-1"></i>
                                {pro}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="col-12">
                          <h6 className="fw-bold text-warning">Cons:</h6>
                          <ul className="list-unstyled">
                            {flowComparison.new.cons.map((con, index) => (
                              <li key={index} className="mb-1 small">
                                <i className="fas fa-minus text-warning me-1"></i>
                                {con}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className="card-footer">
                      <Button
                        className="btn btn-primary w-100"
                        onClick={() => {
                          setCurrentFlow('new')
                          navigate(flowComparison.new.route)
                        }}
                      >
                        Demo Default Flow
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-center mt-8">
                <div className="row">
                  <div className="col-md-4">
                    <Button
                      variant="outline-secondary"
                      onClick={() => navigate('/auth/login')}
                    >
                      ← Back to Login
                    </Button>
                  </div>
                  <div className="col-md-4">
                    <div className="alert alert-light mb-0">
                      <strong>Quick Test:</strong><br />
                      Use email: <code>test@example.com</code><br />
                      Check your email for verification codes
                    </div>
                  </div>
                  <div className="col-md-4">
                    <small className="text-muted">
                      <strong>Toggle Between Flows:</strong><br />
                      Edit <code>src/config.tsx</code> to switch
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SignupFlowDemo 